import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Route, Routes, Navigate } from "react-router-dom";
import api, { fmtNZD, fmtDate, BACKEND_URL } from "@/lib/api";
import BillsPanel from "@/pages/operations/BillsPanel";
import ReportBuilder from "@/pages/operations/ReportBuilder";
import LeaveAdmin from "@/pages/operations/LeaveAdmin";
import PaySauceImport from "@/pages/operations/PaySauceImport";
import ShadowRun from "@/pages/operations/ShadowRun";
import NumberInput from "@/components/NumberInput";
import DateInput from "@/components/DateInput";
import { TimesCell, TimesEditor } from "@/pages/operations/TimesAdmin";
import { AdminTsEditor } from "@/pages/operations/AdminTsEditor";
import { useAuth } from "@/context/AuthContext";
import { byLastName, sortWorkers } from "@/lib/names";
import AwayStrip from "@/components/AwayStrip";

const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);
const typedDelete = (what) => (window.prompt(`${what}\n\nType DELETE to confirm.`) || "").trim() === "DELETE";

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "Admin" ? children : <Navigate to="/operations/timesheets" replace />;
}

export default function OperationsPage() {
  const [pending, setPending] = useState(0);
  const refreshPending = () => api.get("/leave/pending-count").then(r => setPending(r.data.count)).catch(() => {});
  useEffect(() => {
    refreshPending();
    window.addEventListener("leave-changed", refreshPending);
    return () => window.removeEventListener("leave-changed", refreshPending);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="operations-page">
      <h1 className="text-2xl">Operations</h1>
      <AwayStrip />
      <div className="flex gap-2 border-b border-neutral-800 pb-2 flex-wrap">
        <NavLink to="/operations/timesheets" data-testid="op-timesheets" className={({isActive})=>`px-3 py-2 rounded text-sm ${isActive?"bg-silver text-black":"text-white"}`}>Timesheets</NavLink>
        <NavLink to="/operations/bills" data-testid="op-bills" className={({isActive})=>`px-3 py-2 rounded text-sm ${isActive?"bg-silver text-black":"text-white"}`}>Bills</NavLink>
        <NavLink to="/operations/report-builder" data-testid="op-rb" className={({isActive})=>`px-3 py-2 rounded text-sm ${isActive?"bg-silver text-black":"text-white"}`}>Report Builder</NavLink>
        <NavLink to="/operations/payroll" data-testid="op-payroll" className={({isActive})=>`px-3 py-2 rounded text-sm ${isActive?"bg-silver text-black":"text-white"}`}>Payroll</NavLink>
        <NavLink to="/operations/leave" data-testid="op-leave" className={({isActive})=>`px-3 py-2 rounded text-sm ${isActive?"bg-silver text-black":"text-white"}`}>Leave requests{pending > 0 && <span className="ml-1.5 inline-block min-w-[1.25rem] px-1 rounded-full bg-amber-500 text-black text-xs font-bold text-center" data-testid="leave-pending-badge">{pending}</span>}</NavLink>
      </div>
      <Routes>
        <Route index element={<Navigate to="/operations/timesheets" replace />} />
        <Route path="timesheets" element={<Timesheets />} />
        <Route path="bills" element={<BillsPanel />} />
        <Route path="invoices" element={<AdminOnly><Invoices /></AdminOnly>} />
        <Route path="report-builder" element={<ReportBuilder />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="leave" element={<LeaveAdmin onChange={refreshPending} />} />
        {/* Anything deeper/unknown → clean up and go to Timesheets */}
        <Route path="*" element={<Navigate to="/operations/timesheets" replace />} />
      </Routes>
    </div>
  );
}

/** Status label used in the filter — maps to the status values the backend sets. */
const TS_STATUSES = ["Submitted", "Pending", "Accepted", "Rejected", "Paid"];
const SUBMITTED_LIKE = new Set(["Submitted", "Pending"]);

function Timesheets() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [editBreak, setEditBreak] = useState(null);
  const [editTimes, setEditTimes] = useState(null);
  const [editTs, setEditTs] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  // Filters — default to Submitted/Pending so Luke doesn't see Paid history
  const [statusFilter, setStatusFilter] = useState("Submitted");
  const [workerFilter, setWorkerFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [leave, setLeave] = useState([]);

  async function load() {
    const { data } = await api.get("/timesheets");
    setList(data); setSelected(new Set());
    api.get("/leave/lines").then(r => setLeave(r.data)).catch(() => {});
    const s = new Date(); s.setDate(s.getDate() - 56); const e = new Date(); e.setDate(e.getDate() + 7);
    api.get("/leave-tracker/ph-lines", { params: { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) } })
      .then(r => setLeave(l => [...l, ...r.data.map(p => ({ ...p, type: p.label, leave_id: `ph-${p.worker_id}`, ph: true }))])).catch(() => {});
  }
  useEffect(() => { load(); }, []);

  const workers = useMemo(() => {
    const names = Array.from(new Set(list.filter(t => !t.worker_archived).map(t => t.worker_name).filter(Boolean))).sort(byLastName);
    return names;
  }, [list]);

  const visible = useMemo(() => list.filter(t => {
    if (statusFilter === "Submitted") {
      if (!SUBMITTED_LIKE.has(t.status) || t.worker_archived) return false;
    } else if (statusFilter && statusFilter !== "All") {
      if (t.status !== statusFilter) return false;
    }
    if (workerFilter && t.worker_name !== workerFilter) return false;
    if (fromDate && (t.work_date || "") < fromDate) return false;
    if (toDate && (t.work_date || "") > toDate) return false;
    return true;
  }), [list, statusFilter, workerFilter, fromDate, toDate]);

  function toggle(id) {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  }
  function toggleAll(checked) {
    if (checked) setSelected(new Set(visible.map(t => t.id)));
    else setSelected(new Set());
  }
  const allChecked = visible.length > 0 && visible.every(t => selected.has(t.id));

  async function bulk(status) {
    if (selected.size === 0) return;
    const label = status === "Accepted" ? "Approve" : "Reject";
    if (!window.confirm(`${label} ${selected.size} timesheet${selected.size===1?"":"s"}?`)) return;
    setMsg(""); setErr("");
    try {
      const { data } = await api.post("/timesheets/bulk-status", { ids: [...selected], status });
      setMsg(`${label}d ${data.updated} timesheet${data.updated===1?"":"s"} ✓`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || `${label} failed`);
    }
  }

  async function saveBreak() {
    await api.post(`/timesheets/${editBreak.id}/break`,
                   { break_taken: editBreak.break_taken, break_minutes: editBreak.break_minutes || 30 });
    setEditBreak(null); load();
  }

  return (
    <div className="space-y-3" data-testid="timesheets-section">
      {editTimes && <TimesEditor ts={editTimes} onClose={(saved) => { setEditTimes(null); if (saved) load(); }} />}
      {editTs && <AdminTsEditor key={editTs.id} ts={editTs} onClose={(saved) => { setEditTs(null); if (saved) load(); }} />}
      <div className="card flex gap-2 items-end flex-wrap" data-testid="ts-filters">
        <label className="text-sm">Status
          <select className="input" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} data-testid="ts-filter-status">
            <option value="All">All</option>
            {TS_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
          </select></label>
        <label className="text-sm">Worker
          <select className="input" value={workerFilter} onChange={e=>setWorkerFilter(e.target.value)} data-testid="ts-filter-worker">
            <option value="">All workers</option>
            {workers.map(w=><option key={w} value={w}>{w}</option>)}
          </select></label>
        <label className="text-sm">From<DateInput className="input" data-testid="ts-filter-from" value={fromDate} onChange={e=>setFromDate(e.target.value)} showQuickPicks onQuickPick={({from,to})=>{setFromDate(from);setToDate(to);}}/></label>
        <label className="text-sm">To<DateInput className="input" data-testid="ts-filter-to" value={toDate} onChange={e=>setToDate(e.target.value)}/></label>
        <button className="btn-ghost text-xs" onClick={()=>{setStatusFilter("Submitted");setWorkerFilter("");setFromDate("");setToDate("");}} data-testid="ts-filter-reset">Reset</button>
      </div>

      <div className="card flex gap-2 flex-wrap items-center">
        <button className="btn-silver" onClick={()=>bulk("Accepted")} data-testid="ts-bulk-approve" disabled={selected.size===0}>Approve ({selected.size})</button>
        <button className="btn-ghost" onClick={()=>bulk("Rejected")} data-testid="ts-bulk-reject" disabled={selected.size===0}>Reject ({selected.size})</button>
        {selected.size > 0 && <button className="btn-ghost text-xs" onClick={()=>setSelected(new Set())} data-testid="ts-clear-selection">Clear selection</button>}
        <div className="text-xs opacity-70 ml-auto">{visible.length} shown · {selected.size} selected</div>
      </div>
      {msg && <div className="text-green-400 text-sm" data-testid="ts-msg">{msg}</div>}
      {err && <div className="text-red-500 text-sm" data-testid="ts-err">{err}</div>}

      {editBreak && (
        <div className="card" data-testid="break-editor">
          <h3 className="text-silver">Edit break — {editBreak.worker_name} {fmtDate(editBreak.work_date)}</h3>
          <label className="text-sm flex items-center gap-2 my-2"><input type="checkbox" checked={editBreak.break_taken} onChange={e=>setEditBreak({...editBreak,break_taken:e.target.checked})}/>Unpaid break taken</label>
          <label className="text-sm">Minutes<NumberInput className="input w-24 ml-2 inline-block" value={editBreak.break_minutes||30} onChange={e=>setEditBreak({...editBreak,break_minutes:parseInt(e.target.value||30)})}/></label>
          <div className="flex gap-2 mt-2">
            <button className="btn-silver" onClick={saveBreak} data-testid="btn-save-break">Save</button>
            <button className="btn-ghost" onClick={()=>setEditBreak(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead><tr>
            <th><input type="checkbox" checked={allChecked} onChange={e=>toggleAll(e.target.checked)} data-testid="ts-select-all"/></th>
            <th>Date</th><th>Worker</th><th>Start–End</th><th>Entered</th><th>Paid</th><th>Break</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>{visible.map(t=>(
            <tr key={t.id} data-testid={`timesheet-${t.id}`}>
              <td><input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggle(t.id)} data-testid={`ts-select-${t.id}`}/></td>
              <td>{fmtDate(t.work_date)}</td><td>{t.worker_name}</td>
              <td><TimesCell ts={t} onEdit={setEditTimes} /></td>
              <td>{t.total_hours}</td><td>{t.paid_hours}</td>
              <td>{t.break_taken ? "Yes" : "No"}</td>
              <td><span className="badge badge-grey">{t.status === "Accepted" ? "Approved" : t.status}</span>
                {t.admin_edit && <div className="text-[10px] text-amber-300 mt-0.5" data-testid={`ts-admin-edited-${t.id}`}>edited by {t.admin_edit.by} · {fmtDate(t.admin_edit.at)}</div>}</td>
              <td className="space-x-2 whitespace-nowrap">
                {t.status !== "Paid" && !t.reported && <button className="text-xs hub-link" onClick={()=>{ setEditTs(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`btn-edit-ts-${t.id}`}>Edit</button>}
                {t.status !== "Paid" && !t.reported && <button className="text-xs hub-link" onClick={()=>setEditBreak(t)} data-testid={`btn-edit-break-${t.id}`}>Edit break</button>}
                {t.reported && <span className="text-[11px] text-sky-300" data-testid={`ts-reported-${t.id}`}>Reported — locked</span>}
                {t.status === "Paid" && <span className="text-[11px] opacity-50" data-testid={`ts-paid-locked-${t.id}`}>In pay run — locked</span>}
              </td>
            </tr>
          ))}
          {visible.length===0 && <tr><td colSpan="9" className="opacity-50">No timesheets match the current filters.</td></tr>}</tbody>
        </table>
      </div>
      <LeaveLines leave={leave} worker={workerFilter} from={fromDate} to={toDate} onAdded={load} />
    </div>
  );
}

function AddPHLine({ onAdded }) {
  const [workers, setWorkers] = useState([]); const [hols, setHols] = useState([]);
  const [sel, setSel] = useState(new Set()); const [date, setDate] = useState("");
  const [hrs, setHrs] = useState({}); const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/workers").then(r => setWorkers(sortWorkers(r.data.filter(w => !w.archived)))).catch(() => {});
    const t = new Date(); t.setFullYear(t.getFullYear() - 1); const lo = t.toISOString().slice(0, 10); t.setFullYear(t.getFullYear() + 2); const hi = t.toISOString().slice(0, 10);
    api.get("/public-holidays").then(r => setHols(r.data.filter(h => h.date >= lo && h.date <= hi && ![0, 6].includes(new Date(h.date + "T00:00:00").getDay())))).catch(() => {});
  }, []);
  useEffect(() => {
    if (!date) return;
    [...sel].filter(id => !hrs[id]).forEach(id => api.get("/leave-tracker/ph-hours", { params: { worker_id: id, date } })
      .then(r => setHrs(h => ({ ...h, [id]: { hours: String(r.data.hours), source: r.data.source } }))).catch(() => {}));
  }, [sel, date]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = id => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pickDate = d => { setDate(d); setHrs({}); };
  async function add() {
    setMsg("");
    const hours_by_worker = Object.fromEntries([...sel].map(id => [id, Number(hrs[id]?.hours || 0)]).filter(([, v]) => v > 0));
    try {
      const { data } = await api.post("/leave-tracker/public-holiday-line", { worker_ids: [...sel], date, hours_by_worker });
      setMsg(`Added ${data.added.length} line${data.added.length === 1 ? "" : "s"}` + (data.skipped.length ? ` · skipped ${data.skipped.map(s => `${s.name || s.worker_id} (${s.reason})`).join(", ")}` : ""));
      setSel(new Set()); setHrs({}); onAdded();
    } catch (e) { setMsg(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't add"); }
  }
  return (
    <div className="space-y-2 text-sm border-t border-white/10 pt-2 mt-2" data-testid="ph-add">
      <div className="text-silver">Add a Public Holiday Taken line</div>
      <select className="input !w-auto" value={date} onChange={e => pickDate(e.target.value)} data-testid="ph-add-date"><option value="">Public holiday (last 12 months to next 12 months)…</option>{hols.map(h => <option key={h.id} value={h.date}>{fmtDate(h.date)} {h.name}</option>)}</select>
      <div className="flex gap-3 items-center">
        <label className="flex items-center gap-1"><input type="checkbox" checked={workers.length > 0 && sel.size === workers.length} onChange={e => setSel(e.target.checked ? new Set(workers.map(w => w.id)) : new Set())} data-testid="ph-add-all" />All active staff</label>
        <span className="opacity-60 text-xs">{sel.size} selected</span>
      </div>
      <div className="max-h-64 overflow-y-auto border border-white/10 rounded divide-y divide-white/5" data-testid="ph-add-workers">
        {workers.map(w => (
          <div key={w.id} className="flex items-center gap-2 px-2 py-1 flex-wrap">
            <label className="flex items-center gap-1 min-w-[10rem]"><input type="checkbox" checked={sel.has(w.id)} onChange={() => toggle(w.id)} data-testid={`ph-add-w-${w.id}`} />{w.name}</label>
            {sel.has(w.id) && date && (<>
              <input type="number" step="0.0001" className="input !w-28" value={hrs[w.id]?.hours ?? ""} onChange={e => setHrs(h => ({ ...h, [w.id]: { ...h[w.id], hours: e.target.value } }))} data-testid={`ph-add-h-${w.id}`} />
              <span className="text-[11px] opacity-60" data-testid={`ph-add-src-${w.id}`}>{hrs[w.id]?.source || "…"}</span>
            </>)}
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <button className="btn-silver text-sm" disabled={!sel.size || !date} onClick={add} data-testid="ph-add-save">Add</button>
        {msg && <span data-testid="ph-add-msg">{msg}</span>}
      </div>
      <div className="text-[11px] opacity-60">Paid at their leave rate; nothing comes off any balance. Hours are prefilled from their average paid hours per day worked over the last 4 weeks (normal daily hours if there's no history) — overtype if needed.</div>
    </div>
  );
}

function AddLeaveEntry({ onAdded }) {
  const [workers, setWorkers] = useState([]);
  const [f, setF] = useState({ worker_id: "", type: "Annual leave", from_date: "", to_date: "", hours_per_day: "", note: "", bereavement_kind: "close", medical_certificate: false });
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/workers").then(r => setWorkers(sortWorkers(r.data.filter(w => !w.archived)))).catch(() => {}); }, []);
  const w = workers.find(x => x.id === f.worker_id);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  async function add(override = false) {
    setMsg("");
    const body = { ...f, to_date: f.to_date || f.from_date, hours_per_day: f.hours_per_day ? Number(f.hours_per_day) : null, override };
    try {
      const { data } = await api.post("/leave-tracker/leave-entry", body);
      setMsg(`Added ${data.type.toLowerCase()} for ${data.worker_name}: ${data.working_days} day${data.working_days === 1 ? "" : "s"}, ${data.hours} h`);
      setF(x => ({ ...x, from_date: "", to_date: "", hours_per_day: "", note: "", medical_certificate: false })); onAdded();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "short") {
        if (window.confirm(`${d.message}\n\nAdd anyway? The balance will go negative.`)) add(true);
        else setMsg("Not added.");
        return;
      }
      setMsg(typeof d === "string" ? d : "Couldn't add");
    }
  }
  return (
    <div className="space-y-2 text-sm border-t border-white/10 pt-2 mt-2" data-testid="leave-add">
      <div className="text-silver">Add Annual, Sick or Bereavement leave taken</div>
      <div className="flex flex-wrap gap-2 items-end">
        <label>Worker<select className="input block" value={f.worker_id} onChange={set("worker_id")} data-testid="leave-add-worker"><option value="">Pick a worker…</option>{workers.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
        <label>Leave type<select className="input block" value={f.type} onChange={set("type")} data-testid="leave-add-type">{["Annual leave", "Sick leave", "Bereavement leave"].map(t => <option key={t}>{t}</option>)}</select></label>
        {f.type === "Bereavement leave" && <label>Relative<select className="input block" value={f.bereavement_kind} onChange={set("bereavement_kind")} data-testid="leave-add-kind"><option value="close">Close family</option><option value="other">Other</option></select></label>}
        <label>From<DateInput className="input block" value={f.from_date} onChange={set("from_date")} data-testid="leave-add-from" /></label>
        <label>To (optional)<DateInput className="input block" value={f.to_date} onChange={set("to_date")} data-testid="leave-add-to" /></label>
        <label>Hours per day<input type="number" step="0.25" className="input block !w-28" placeholder={w ? String(w.normal_daily_hours || 8) : "8"} value={f.hours_per_day} onChange={set("hours_per_day")} data-testid="leave-add-hours" /></label>
        <label className="flex-1 min-w-[10rem]">Note (optional)<input className="input block w-full" value={f.note} onChange={set("note")} data-testid="leave-add-note" /></label>
        {f.type === "Sick leave" && <label className="flex items-center gap-1 pb-2"><input type="checkbox" checked={f.medical_certificate} onChange={e => setF(x => ({ ...x, medical_certificate: e.target.checked }))} data-testid="leave-add-cert" />Medical certificate provided</label>}
        <button className="btn-silver text-sm" disabled={!f.worker_id || !f.from_date} onClick={() => add()} data-testid="leave-add-save">Add</button>
      </div>
      {msg && <div data-testid="leave-add-msg">{msg}</div>}
      <div className="text-[11px] opacity-60">Saved as approved and taken off the balance straight away. Weekends and public holidays in the range are skipped. Blank hours = their normal day.</div>
    </div>
  );
}

function LineRow({ l, workers, canEdit, canBackdate, onDone }) {
  const [ed, setEd] = useState(null); const [err, setErr] = useState("");
  const isPH = l.type === "Public holiday";
  const label = l.ph ? l.label : isPH ? `Public Holiday Taken ${fmtDate(l.date).slice(0, 5)}` : `${l.type.replace(" leave", " Leave")} ${fmtDate(l.date).slice(0, 5)}`;
  async function save() {
    setErr("");
    try { await api.put(`/leave-tracker/leave-line/${l.leave_id}`, { hours: Number(ed.hours), ...(isPH ? { date: ed.date, worker_id: ed.worker_id } : {}) }); setEd(null); onDone(); }
    catch (e) { setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save"); }
  }
  async function del(mode) {
    const what = isPH ? "Delete this Public Holiday Taken line?" : mode === "pending" ? "Send this leave back to Pending? Hours go back on the balance." : "Cancel this approved leave? Hours go back on the balance.";
    if (!window.confirm(what)) return;
    await api.delete(`/leave-tracker/leave-line/${l.leave_id}`, { params: { mode } }); onDone();
  }
  return (
    <tr data-testid={`ts-leave-${l.worker_id}-${l.date}`}>
      <td>{ed && isPH ? <input type="date" className="input !w-auto" min={canBackdate ? undefined : new Date().toISOString().slice(0, 10)} value={ed.date} onChange={e => setEd({ ...ed, date: e.target.value })} data-testid="line-edit-date" /> : fmtDate(l.date)}</td>
      <td>{ed && isPH ? <select className="input !w-auto" value={ed.worker_id} onChange={e => setEd({ ...ed, worker_id: e.target.value })} data-testid="line-edit-worker">{workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select> : l.worker_name}</td>
      <td>{label}{l.medical_certificate && <span className="text-[11px] text-green-400 ml-1" data-testid={`line-cert-${l.leave_id}-${l.date}`}>· Medical certificate provided</span>}{!isPH && !l.ph && ed && <div className="text-[10px] opacity-60">Hours per day for the whole request</div>}</td>
      <td>{ed ? <input type="number" step="0.0001" className="input !w-24" value={ed.hours} onChange={e => setEd({ ...ed, hours: e.target.value })} data-testid="line-edit-hours" /> : l.hours}{err && <div className="text-red-400 text-xs">{err}</div>}</td>
      <td className="whitespace-nowrap space-x-2">{canEdit && !l.ph && (ed
        ? <><button className="hub-link text-xs" onClick={save} data-testid="line-edit-save">Save</button><button className="hub-link text-xs" onClick={() => { setEd(null); setErr(""); }} data-testid="line-edit-cancel">Cancel</button></>
        : <><button className="hub-link text-xs" onClick={() => setEd({ hours: l.hours, date: l.date, worker_id: l.worker_id })} data-testid={`line-edit-${l.leave_id}-${l.date}`}>Edit</button>
            <button className="hub-link text-xs text-red-400" onClick={() => del("cancel")} data-testid={`line-del-${l.leave_id}-${l.date}`}>{isPH ? "Delete" : "Cancel leave"}</button>
            {!isPH && <button className="hub-link text-xs" onClick={() => del("pending")} data-testid={`line-pending-${l.leave_id}-${l.date}`}>Back to Pending</button>}</>)}
        {l.ph && <span className="text-[10px] opacity-50">auto</span>}</td>
    </tr>
  );
}

function LeaveLines({ leave, worker, from, to, onAdded }) {
  const { user } = useAuth();
  const canEdit = user?.role === "Admin" || user?.role === "Manager";
  const [workers, setWorkers] = useState([]);
  useEffect(() => { if (canEdit) api.get("/workers").then(r => setWorkers(sortWorkers(r.data.filter(w => !w.archived)))).catch(() => {}); }, [canEdit]);
  const rows = leave.filter(l => (!worker || l.worker_name === worker) && (!from || l.date >= from) && (!to || l.date <= to))
    .sort((a, b) => (b.date || "").localeCompare(a.date || "") || byLastName(a.worker_name, b.worker_name));
  return (
    <div className="card" data-testid="ts-leave-lines">
      <h3 className="text-silver mb-1">Approved leave</h3>
      <div className="text-xs opacity-60 mb-2">One line per leave day, for keying into PaySauce. Not included in pay runs or cost reports. "auto" lines are worked out by the Hub; add a line below to fix one.</div>
      <div className="overflow-x-auto"><table className="table"><thead><tr><th>Date</th><th>Worker</th><th>Leave</th><th>Hours</th><th /></tr></thead>
        <tbody>{rows.map(l => <LineRow key={`${l.leave_id}-${l.date}`} l={l} workers={workers} canEdit={canEdit} canBackdate={user?.role === "Admin"} onDone={onAdded} />)}
          {rows.length === 0 && <tr><td colSpan="5" className="opacity-50">No approved leave for these filters.</td></tr>}</tbody></table></div>
      {user?.role === "Admin" && <AddPHLine onAdded={onAdded} />}
      {user?.role === "Admin" && <AddLeaveEntry onAdded={onAdded} />}
    </div>
  );
}

function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [msg, setMsg] = useState("");
  async function load(){ const {data} = await api.get("/invoices"); setList(data); setSelected(new Set()); }
  useEffect(() => { load(); }, []);
  async function del(ids) {
    const rows = list.filter(x => ids.includes(x.id));
    const finals = rows.filter(x => x.status !== "Draft");
    if (finals.length) {
      let chk = [];
      try { chk = (await api.post("/invoices/delete-check", { ids: finals.map(x => x.id) })).data; } catch (e) { setMsg(errText(e, "Admin only")); return; }
      const lines = chk.map(c => `• #${c.number} · ${c.client_name || "—"} · ${c.project_name || "—"} · ${fmtNZD(c.amount_ex)}${c.warning ? `\n   ⚠ WARNING: ${c.warning}` : ""}`).join("\n");
      const drafts = rows.length - finals.length;
      if (window.prompt(`This permanently removes the invoice record. Type DELETE to confirm.\n\n${finals.length} finalised invoice(s):\n${lines}${drafts ? `\n\nPlus ${drafts} draft(s) to Deleted items.` : ""}\n\nA permanent audit entry (who, when, client, project, amount, number) is kept.`) !== "DELETE") { setMsg("Not deleted."); return; }
      try { const { data } = await api.post("/invoices/bulk-delete", { ids, confirm_text: "DELETE", include_finalised: true }); setMsg(`Permanently deleted ${data.finalised_deleted} finalised invoice(s)${data.soft_deleted ? `; ${data.soft_deleted} draft(s) moved to Deleted items` : ""}. Logged in the invoice deletion log.`); load(); }
      catch (e) { setMsg(errText(e, "Delete failed")); }
      return;
    }
    const total = rows.reduce((s, x) => s + Number(x.amount_ex || 0), 0);
    if (!typedDelete(`Delete ${rows.length} draft invoice(s), ${fmtNZD(total)} ex GST?\nThey go to Deleted items and can be restored for 30 days.`)) return;
    try { const { data } = await api.post("/invoices/bulk-delete", { ids, confirm_text: "DELETE" }); setMsg(`Deleted ${data.soft_deleted} draft invoice(s) — restore from Deleted items within 30 days.`); load(); }
    catch (e) { setMsg(errText(e, "Delete failed")); }
  }
  async function downloadInvoicePdf(id) {
    const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  }
  function toggle(id){ const s=new Set(selected); s.has(id)?s.delete(id):s.add(id); setSelected(s); }
  function toggleAll(c){ setSelected(c ? new Set(list.map(x=>x.id)) : new Set()); }
  const allChecked = list.length > 0 && list.every(x => selected.has(x.id));
  return (
    <div className="card">
      <div className="flex gap-2 flex-wrap items-center mb-2">
        <span className="text-xs opacity-70">{list.length} invoices · {selected.size} selected</span>
        {selected.size > 0 && <button className="btn-ghost text-xs" onClick={()=>setSelected(new Set())} data-testid="inv-clear-selection">Clear selection</button>}
        {isAdmin && selected.size > 0 && <button className="btn-ghost text-xs !text-red-400" onClick={()=>del([...selected])} data-testid="inv-bulk-delete">Delete selected</button>}
      </div>
      {msg && <div className="text-sm text-silver mb-2" data-testid="inv-msg">{msg}</div>}
      <table className="table"><thead><tr>
        <th><input type="checkbox" checked={allChecked} onChange={e=>toggleAll(e.target.checked)} data-testid="inv-select-all"/></th>
        <th>Client</th><th>Project</th><th>Amount ex GST</th><th>Status</th><th></th></tr></thead>
        <tbody>{list.map(i => (
          <tr key={i.id} data-testid={`invoice-${i.id}`}>
            <td><input type="checkbox" checked={selected.has(i.id)} onChange={()=>toggle(i.id)} data-testid={`inv-select-${i.id}`}/></td>
            <td>{i.client_name}</td><td>{i.project_name||"—"}</td><td>{fmtNZD(i.amount_ex)}</td>
            <td><span className="badge badge-grey">{i.status}</span></td>
            <td><span className="flex gap-3 items-center"><a href="#" onClick={(e)=>{e.preventDefault(); downloadInvoicePdf(i.id);}} className="hub-link text-xs">Download PDF</a>
              {isAdmin && <button className="text-xs text-red-400" onClick={()=>del([i.id])} data-testid={`inv-delete-${i.id}`}>Delete</button>}</span></td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan="6" className="opacity-50">No invoices yet. Use Report Builder to create.</td></tr>}</tbody></table>
      {isAdmin && <InvoiceDeletionLog key={list.length} />}
    </div>
  );
}

function InvoiceDeletionLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/invoices/deletion-log").then(r => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <details className="mt-3 text-xs" data-testid="inv-deletion-log">
      <summary className="cursor-pointer opacity-70">Deleted finalised invoices log ({rows.length})</summary>
      <ul className="mt-1 space-y-1">{rows.map(r => (
        <li key={r.id}>{fmtDate(r.at)} · {r.by} deleted #{r.invoice_number} · {r.client_name || "—"} · {r.project_name || "—"} · {fmtNZD(r.amount_ex)}{r.warning ? ` · ⚠ ${r.warning}` : ""}</li>
      ))}</ul>
    </details>
  );
}

function Payroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [runMsg, setRunMsg] = useState("");
  const [preview, setPreview] = useState(null);
  const [weekStart, setWeekStart] = useState("");
  const [runs, setRuns] = useState([]);
  const [calc, setCalc] = useState({ pay_rate: 50, oncosts: [] });
  const [calcResult, setCalcResult] = useState(null);
  const [selectedRuns, setSelectedRuns] = useState(new Set());
  async function doPreview() { const { data } = await api.post("/payruns/preview", { week_start: weekStart }); setPreview(data); }
  async function confirm() {
    if (!window.confirm("Confirm pay run and lock timesheets?")) return;
    await api.post("/payruns/confirm", { week_start: weekStart }); setPreview(null); loadRuns();
  }
  async function loadRuns(){ const {data} = await api.get("/payruns"); setRuns(data); setSelectedRuns(new Set()); }
  async function runCalc(){ const {data} = await api.post("/payroll/cost-rate", calc); setCalcResult(data); }
  useEffect(() => { loadRuns(); }, []);
  function toggleRun(id){ const s=new Set(selectedRuns); s.has(id)?s.delete(id):s.add(id); setSelectedRuns(s); }
  function toggleAllRuns(c){ setSelectedRuns(c ? new Set(runs.map(r=>r.id)) : new Set()); }
  const allRunsChecked = runs.length > 0 && runs.every(r => selectedRuns.has(r.id));
  async function deleteRuns() {
    const rows = runs.filter(r => selectedRuns.has(r.id));
    const total = rows.reduce((s, r) => s + Number(r.totals?.net || 0), 0);
    if (!typedDelete(`Delete ${rows.length} confirmed pay run(s), total net ${fmtNZD(total)}?\nTheir timesheets go back to Accepted. This can't be undone.`)) return;
    try { const { data } = await api.post("/payruns/bulk-delete", { ids: rows.map(r => r.id), confirm_text: "DELETE" }); setRunMsg(`Deleted ${data.deleted} pay run(s); ${data.timesheets_unlocked} timesheet(s) back to Accepted.`); loadRuns(); }
    catch (e) { setRunMsg(errText(e, "Delete failed")); }
  }
  async function downloadBankCsv(id) {
    const r = await api.get(`/payruns/${id}/bank.csv`, { responseType: "blob" });
    const u = URL.createObjectURL(r.data); const a = document.createElement("a");
    a.href = u; a.download = "bank.csv"; a.click();
  }
  async function downloadSummaryPdf(id) {
    const r = await api.get(`/payruns/${id}/summary.pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  }
  return (
    <div className="space-y-3">
      {isAdmin && <PaySauceImport />}
      {isAdmin && <ShadowRun />}
      <div className="card flex gap-2 items-end">
        <label className="text-sm">Week starting (Monday)
          <DateInput className="input" data-testid="payrun-week-start" value={weekStart} onChange={e=>setWeekStart(e.target.value)}/></label>
        <button className="btn-silver" onClick={doPreview} data-testid="btn-payroll-preview">Preview pay run</button>
      </div>
      {preview && (
        <div className="card" data-testid="payroll-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="text-silver">Week {fmtDate(preview.week_start)} — {fmtDate(preview.week_end)}, pay date {fmtDate(preview.pay_date)}</div>
            <button className="btn-silver" onClick={confirm} data-testid="btn-payroll-confirm">Confirm Pay Run</button>
          </div>
          <h4 className="text-silver mt-1 mb-1" data-testid="payrun-section-hourly">Hourly</h4>
          <table className="table"><thead><tr><th>Worker</th><th>Hours</th><th>Gross</th><th>PAYE</th><th>SL</th><th>KS(ee)</th><th>Net</th></tr></thead>
            <tbody>{(preview.rows||[]).map(r=>(
              <tr key={r.worker_id} data-testid={`payrun-hourly-${r.worker_id}`}><td>{r.worker_name}</td><td>{r.hours}</td>
                <td>{fmtNZD(r.gross)}</td><td>{fmtNZD(r.paye)}</td><td>{fmtNZD(r.student_loan)}</td>
                <td>{fmtNZD(r.employee_kiwisaver)}</td><td className="font-bold">{fmtNZD(r.net)}</td></tr>
            ))}
            {(preview.rows||[]).length===0 && <tr><td colSpan="7" className="opacity-50">No hourly workers this week.</td></tr>}</tbody></table>

          <h4 className="text-silver mt-4 mb-1" data-testid="payrun-section-salaried">Salaried</h4>
          <table className="table"><thead><tr><th>Worker</th><th>Hours</th><th>Gross</th><th>PAYE</th><th>SL</th><th>KS(ee)</th><th>Net</th></tr></thead>
            <tbody>{(preview.salaried_rows||[]).map(r=>(
              <tr key={r.worker_id} data-testid={`payrun-salaried-${r.worker_id}`}><td>{r.worker_name}</td><td>{r.hours}</td>
                <td>{fmtNZD(r.gross)}</td><td>{fmtNZD(r.paye)}</td><td>{fmtNZD(r.student_loan)}</td>
                <td>{fmtNZD(r.employee_kiwisaver)}</td><td className="font-bold">{fmtNZD(r.net)}</td></tr>
            ))}
            {(preview.salaried_rows||[]).length===0 && <tr><td colSpan="7" className="opacity-50">No salaried workers this week.</td></tr>}</tbody></table>

          <h4 className="text-silver mt-4 mb-1" data-testid="payrun-section-contractors">Contractors</h4>
          <table className="table"><thead><tr>
            <th>Contractor</th><th>Hours</th><th>Rate</th><th>Gross</th>
            <th>WH %</th><th>Withholding $</th><th>GST</th><th>Net paid</th><th>Cost ex GST</th></tr></thead>
            <tbody>{(preview.contractor_rows||[]).map(r=>(
              <tr key={r.worker_id} data-testid={`payrun-contractor-${r.worker_id}`}>
                <td>{r.worker_name}</td><td>{r.hours}</td>
                <td>{fmtNZD(r.contract_rate)}</td><td>{fmtNZD(r.gross)}</td>
                <td>{r.withholding_pct}%</td><td>{fmtNZD(r.withholding)}</td>
                <td>{r.gst_registered ? fmtNZD(r.gst) : "—"}</td>
                <td className="font-bold">{fmtNZD(r.paid_to_contractor)}</td>
                <td>{fmtNZD(r.company_cost_ex_gst)}</td></tr>
            ))}
            {(preview.contractor_rows||[]).length===0 && <tr><td colSpan="9" className="opacity-50">No contractors this week.</td></tr>}</tbody></table>
          {(preview.contractor_rows||[]).length>0 && (
            <div className="mt-2 text-sm" data-testid="payrun-contractor-ird-total">
              Withholding to pay IRD: <b className="text-silver">{fmtNZD(preview.totals?.contractor_withholding)}</b>
            </div>
          )}
        </div>
      )}
      {runs.length>0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-silver">Confirmed pay runs</h3>
            <div className="flex items-center gap-3">
              <span className="text-xs opacity-70">{selectedRuns.size} selected</span>
              {isAdmin && selectedRuns.size > 0 && <button className="btn-ghost text-xs !text-red-400" onClick={deleteRuns} data-testid="payrun-bulk-delete">Delete selected pay runs</button>}
            </div>
          </div>
          {runMsg && <div className="text-sm text-silver mb-2" data-testid="payrun-msg">{runMsg}</div>}
          <table className="table"><thead><tr>
            <th><input type="checkbox" checked={allRunsChecked} onChange={e=>toggleAllRuns(e.target.checked)} data-testid="payrun-select-all"/></th>
            <th>Week</th><th>Pay date</th><th>Total net</th><th>Downloads</th></tr></thead>
          <tbody>{runs.map(r=>(
            <tr key={r.id} data-testid={`payrun-${r.id}`}>
              <td><input type="checkbox" checked={selectedRuns.has(r.id)} onChange={()=>toggleRun(r.id)} data-testid={`payrun-select-${r.id}`}/></td>
              <td>{fmtDate(r.week_start)}</td><td>{fmtDate(r.pay_date)}</td>
              <td>{fmtNZD(r.totals?.net)}</td>
              <td className="flex gap-2 text-xs">
                <a href="#" onClick={e=>{e.preventDefault(); downloadBankCsv(r.id);}}>Bank CSV</a>
                <a href="#" onClick={e=>{e.preventDefault(); downloadSummaryPdf(r.id);}}>Summary PDF</a>
              </td></tr>
          ))}</tbody></table>
        </div>
      )}
      <div className="card" data-testid="cost-rate-calc">
        <h3 className="text-silver mb-2">Cost-rate calculator</h3>
        <div className="flex gap-2 items-end">
          <label className="text-sm">Pay rate ($/hr)<NumberInput className="input" value={calc.pay_rate} onChange={e=>setCalc({...calc,pay_rate:parseFloat(e.target.value||0)})}/></label>
          <button className="btn-silver" onClick={runCalc}>Calculate</button>
        </div>
        {calcResult && (
          <div className="mt-2 text-sm">
            <div>On-cost %: <b>{calcResult.total_oncost_pct}%</b></div>
            <div>On-cost per hour: <b>{fmtNZD(calcResult.oncost_per_hour)}</b></div>
            <div>Calculated cost rate: <b className="text-silver">{fmtNZD(calcResult.cost_rate)}</b></div>
          </div>
        )}
      </div>
    </div>
  );
}
